import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articleList } from "@/mocks/articles";
import { animeList } from "@/mocks/anime";
import { articleCategories, siteInfo } from "@/mocks/site";
import type { Article, AnimeWork } from "@/types";

export default function ArticleDetail() {
  const { id } = useParams();
  const index = articleList.findIndex((item) => item.id === id);
  const article = (index >= 0 ? articleList[index] : undefined) as Article | undefined;
  const category = article ? articleCategories.find((cat) => cat.id === article.category) : undefined;
  const prev = index > 0 ? (articleList[index - 1] as Article) : undefined;
  const next =
    index >= 0 && index < articleList.length - 1 ? (articleList[index + 1] as Article) : undefined;
  const relatedWorks = article
    ? (animeList.filter((item) => article.related.includes(item.id)) as AnimeWork[])
    : [];
  const moreArticles = article
    ? (articleList.filter((item) => item.id !== article.id).slice(0, 2) as Article[])
    : [];

  useSeo({
    title: article ? `${article.title} | 樱花动漫社` : "文章未找到 | 樱花动漫社",
    description: article ? article.excerpt : "该文章暂未找到，返回资讯专栏浏览其他内容。",
    keywords: article ? `${article.tags.join(",")},${article.categoryName},动漫资讯` : "动漫资讯",
    path: article ? `/articles/${article.id}` : "/articles",
    image: article?.cover,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            url: `${siteInfo.domain}/articles/${article.id}`,
            datePublished: article.date,
            dateModified: article.updated,
            inLanguage: "zh-CN",
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: siteInfo.name },
            publisher: { "@type": "Organization", name: siteInfo.name, url: siteInfo.domain },
            articleSection: article.categoryName,
            mainEntityOfPage: `${siteInfo.domain}/articles/${article.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
              { "@type": "ListItem", position: 2, name: "资讯专栏", item: `${siteInfo.domain}/articles` },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${siteInfo.domain}/articles/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-700 text-foreground-950">这篇文章暂未找到</h1>
        <p className="mt-3 text-sm text-foreground-600">回到资讯专栏，看看其他内容吧。</p>
        <Link
          to="/articles"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回资讯专栏
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "资讯专栏", to: "/articles" }, { label: article.title }]} />

      <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-[1fr_280px]">
        <article className="min-w-0 max-w-reading">
          <Link
            to={`/articles?category=${article.category}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900 transition-colors hover:bg-accent-200"
          >
            <i className="ri-price-tag-3-line" aria-hidden="true"></i>
            {article.categoryName}
          </Link>

          <h1 className="mt-4 font-heading text-3xl font-700 leading-snug tracking-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" aria-hidden="true"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              发布于 {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line" aria-hidden="true"></i>
              更新于 {article.updated}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true"></i>
              {article.readMinutes} 分钟阅读
            </span>
          </div>

          <div className="mt-7 overflow-hidden rounded-xl border border-background-200 bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              width={1200}
              height={675}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <p className="mt-8 rounded-xl border-l-4 border-primary-400 bg-primary-50 p-5 text-sm leading-7 text-foreground-700 md:text-base">
            {article.excerpt}
          </p>

          <div className="mt-10 space-y-10">
            {article.sections.map((section, sectionIndex) => (
              <section key={section.heading} id={`sec-${sectionIndex}`} className="scroll-mt-24">
                <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-700 md:text-base">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 14)}>{paragraph}</p>
                  ))}
                </div>
                {section.list && (
                  <ul className="mt-5 space-y-2.5">
                    {section.list.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700 md:text-base"
                      >
                        <i className="ri-check-double-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {section.quote && (
                  <blockquote className="mt-5 rounded-xl bg-secondary-50 p-5 text-sm italic leading-7 text-foreground-700 md:text-base">
                    {section.quote}
                  </blockquote>
                )}
              </section>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-700">
                #{tag}
              </span>
            ))}
          </div>

          <nav className="mt-12 grid grid-cols-1 gap-4 border-t border-background-200 pt-8 sm:grid-cols-2" aria-label="文章导航">
            {prev ? (
              <Link
                to={`/articles/${prev.id}`}
                className="group flex flex-col rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-arrow-left-line" aria-hidden="true"></i> 上一篇
                </span>
                <span className="mt-2 font-heading text-sm font-600 text-foreground-950 group-hover:text-primary-700">
                  {prev.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link
                to={`/articles/${next.id}`}
                className="group flex flex-col items-end rounded-xl border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300"
              >
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  下一篇 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
                <span className="mt-2 font-heading text-sm font-600 text-foreground-950 group-hover:text-primary-700">
                  {next.title}
                </span>
              </Link>
            )}
          </nav>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to={`/articles?category=${article.category}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-500 text-foreground-800 transition-colors hover:bg-background-100"
            >
              <i className="ri-folder-line" aria-hidden="true"></i>
              更多「{category?.name}」
            </Link>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回资讯专栏
            </Link>
          </div>
        </article>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-600 text-foreground-950">本文目录</h2>
            <nav aria-label="文章目录" className="mt-4">
              <ol className="space-y-2.5 text-sm">
                {article.sections.map((section, sectionIndex) => (
                  <li key={section.heading}>
                    <a
                      href={`#sec-${sectionIndex}`}
                      className="flex items-start gap-2 text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      <span className="mt-0.5 text-xs text-foreground-400">
                        {String(sectionIndex + 1).padStart(2, "0")}
                      </span>
                      {section.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          {relatedWorks.length > 0 && (
            <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-600 text-foreground-950">文中提到的作品</h2>
              <ul className="mt-4 space-y-3">
                {relatedWorks.map((work) => (
                  <li key={work.id}>
                    <Link to={`/works/${work.id}`} className="group flex gap-3">
                      <img
                        src={work.cover}
                        alt={`《${work.title}》封面`}
                        width={80}
                        height={104}
                        className="h-[68px] w-[52px] shrink-0 rounded-md object-cover object-top"
                      />
                      <span className="flex flex-col">
                        <span className="font-heading text-sm font-600 text-foreground-900 transition-colors group-hover:text-primary-700">
                          {work.title}
                        </span>
                        <span className="mt-1 text-xs text-foreground-500">
                          {work.year} · {work.episodes} 集
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {moreArticles.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">继续阅读</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {moreArticles.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}